import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import { useTheme } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'; // Import Tooltip
import { styled } from '@mui/material/styles'; // Import styled for custom tooltip
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { ReactComponent as LikeDisabledIcon } from '../../assets/images/like-disabled-icon.svg';
import { ReactComponent as LikeEnabledIcon } from '../../assets/images/like-enabled-icon.svg';
import { ReactComponent as DislikeDisabledIcon } from '../../assets/images/dislike-disabled-icon.svg';
import { ReactComponent as DislikeEnabledIcon } from '../../assets/images/dislike-enabled-icon.svg';
import { ReactComponent as TermsIcon } from '../../assets/images/terms-icon.svg'; // Import the TermsIcon
import { ReactComponent as CouponIcon } from '../../assets/images/coupon-icon.svg'; // Import the CouponIcon
import { ReactComponent as CopyIcon } from '../../assets/images/copy-icon-colored.svg';
import LaunchIcon from '@mui/icons-material/Launch';
import couponBackgroundLtr from '../../assets/images/coupon-background-ltr.svg';
import couponBackgroundRtl from '../../assets/images/coupon-background-rtl.svg'; 
import { LANGUAGES } from '../../constants';
import useCouponInteraction from '../../hooks/useCouponInteraction';

const MobileCoupon = ({ coupon, language }) => {
  const { t } = useTranslation(); // Initialize the translation hook
  const theme = useTheme(); // Access the theme to check the direction
  const [tooltipOpen, setTooltipOpen] = useState(false); // State for tooltip visibility
  const [termsTooltipOpen, setTermsTooltipOpen] = useState(false); // State for terms tooltip visibility
  const { isLiked, isDisliked, canInteract, handleLike, handleDislike, handleTrackClick, handleCopyCode } = useCouponInteraction(coupon);

  const storeName = language === 'AR' ? coupon.store_name_arabic : coupon.store_name_english;
  const terms = language === 'AR' ? coupon.offer_terms_arabic : coupon.offer_terms_english;

  const params = new URLSearchParams(window.location.search);
  const PLATFORM = params.get("platform");

  const handleCopy = async () => {
    // await navigator.clipboard.writeText(coupon.promo_code); // Copy promo code to clipboard

    if (window.FlutterClipboard) {
      window.FlutterClipboard.postMessage(coupon.promo_code);
    } else {
      await navigator.clipboard.writeText(coupon.promo_code);
    }

    handleCopyCode(); // Track the copy in Firestore
    setTooltipOpen(true); // Show the tooltip
    setTimeout(() => {
      setTooltipOpen(false); // Hide the tooltip after 2 seconds
    }, 2000);
  };

  const handleShare = async () => {
    if (window.flutter_inappwebview) {
      // Primary method: Use Flutter bridge if available
      console.log("Sharing coupon via Flutter bridge");
      
      window.flutter_inappwebview.callHandler('FlutterShare', {
        language: language,
        store: storeName,
        code: coupon.promo_code,
        offer: terms
      });
    } else {
      // Fallback method: Use URL scheme that can be intercepted by the app
      console.log("Sharing coupon via URL interception");
      
      // Create a URL with the coupon data as query parameters
      const shareUrl = new URL("https://blupons.com/");
      shareUrl.searchParams.append("language", language);
      shareUrl.searchParams.append("store", encodeURIComponent(storeName));
      shareUrl.searchParams.append("code", encodeURIComponent(coupon.promo_code));
      shareUrl.searchParams.append("offer", encodeURIComponent(terms));
      
      // Navigate to the URL (will be intercepted by the app)
      window.location.href = shareUrl.toString();
    }
  };

  const handleTermsClick = () => {
    setTermsTooltipOpen(true); // Show the tooltip
    setTimeout(() => {
      setTermsTooltipOpen(false); // Hide the tooltip after 2 seconds
    }, 5000);
  };

  const handleCouponClick = () => {
    handleTrackClick(); // Track the click in Firestore
    window.open(coupon.store_url, '_blank'); // Open the coupon URL in a new tab
  };

  const getLikeIcon = () => {
    return isLiked ? <LikeEnabledIcon /> : <LikeDisabledIcon />;
  };

  const getDislikeIcon = () => {
    return isDisliked ? <DislikeEnabledIcon /> : <DislikeDisabledIcon />;
  };

  const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.dark,
      boxShadow: theme.shadows[1],
      fontSize: 15,
    },
  }));

  return (
    <Box
      sx={{
        position: 'relative', // Required for the aspect ratio
        width: '100%', // Responsive width to fit the grid
        aspectRatio: '35 / 12', // Width is 5 times the height
        mb: 1,
        bgcolor: 'transparent',
        boxShadow: 0,
        borderRadius: 0, // Optional: Add rounded corners
        overflow: 'hidden', // Ensure content stays within the box
        backgroundImage: LANGUAGES[language].dir == 'rtl' ? `url(${couponBackgroundRtl})` : `url(${couponBackgroundLtr})`, // Use the imported image
        backgroundSize: 'cover', // Ensure the image covers the entire box
        backgroundPosition: 'center', // Center the background image
      }}
    >
      {/* Content Container */}
      <Box
        sx={{
          position: 'absolute', // Position content inside the aspect ratio box
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        {/* Left Section: Store Logo */}
        <Box
          sx={{
            width: '30%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'transparent',
            p: 1,
            position: 'relative', // Required for positioning the open link icon
            textDecoration: 'none', // Removes underline from the link
          }}
        >
          {/* Open Link Icon */}
          <Box
            onClick={handleCouponClick}
            sx={{
              position: 'absolute',
              top: 8, // Adjust the distance from the top
              right: language.dir === 'rtl' ? 8 : 0,
              left: language.dir === 'rtl' ? 8 : 0,
              width: 32, // Size of the icon
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'transparent', // Background color for the icon
              boxShadow: 0, // Adds a subtle shadow
              zIndex: 1, // Ensures the icon is above other elements
            }}
          >
            <LaunchIcon
              style={{
                width: 20,
                height: 20,
                color: 'grey', // Ensures the icon is always grey
                pointerEvents: 'none', // Prevents hover or click effects
              }}
            />
          </Box>
          {PLATFORM &&
            <Box
              onClick={handleShare}
              sx={{
                position: 'absolute',
                bottom: 8, 
                right: language.dir === 'rtl' ? 8 : 0,
                left: language.dir === 'rtl' ? 8 : 0,
                width: 32, 
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'transparent', 
                boxShadow: 0,
                zIndex: 1, 
              }}
            >
              <ShareIcon
                style={{
                  width: 20,
                  height: 20,
                  color: 'grey', 
                  pointerEvents: 'none', 
                }}
              />
            </Box>
          }


          {/* Circular Logo Container */}
          <Box
            sx={{
              width: 85, // Fixed width for the circular container
              height: 85, // Fixed height for the circular container
              minWidth: 85,
              borderRadius: '50%', // Makes the container circular
              border: '1px solid', // Adds a border
              borderColor: 'grey.300', // Border color
              bgcolor: 'white', // Background color for the circle
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden', // Ensures the logo stays within the circle
            }}
          >
            <Box
              component="img"
              src={coupon.logo}
              alt={storeName}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain', // Ensures the logo fits within the circle
              }}
            />
          </Box>
        </Box>

        {/* Right Section: Store Details */}
        <Box
          sx={{
            width: '70%',
            p: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: 16,
              fontWeight: 500,
              mb: 1,
              color: 'text.primary',
            }}
          >
            {storeName}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5, // Space between the icon and text
              mb: 1,
            }}
          >
            <TermsIcon
              style={{
                width: 18,
                height: 18,
                flexShrink: 0, // Prevents the icon from shrinking
              }}
            />
            <LightTooltip
              title={terms} // Full terms text
              open={termsTooltipOpen} // Control visibility
              disableFocusListener
              disableHoverListener
              disableTouchListener
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: 14,
                  fontWeight: 300,
                  color: 'text.secondary',
                  display: 'inline-block', // Ensures the text is treated as a single line
                  whiteSpace: 'nowrap', // Restricts the text to one line
                  overflow: 'hidden', // Hides overflow text
                  textOverflow: 'ellipsis', // Adds ellipsis for overflow text
                  maxWidth: 150, // Restricts the width of the text container
                  cursor: 'pointer', // Indicates the text is clickable
                }}
                onClick={handleTermsClick} // Show tooltip on click
              >
                {terms}
              </Typography>
            </LightTooltip>
          </Box>

          {/* Promo Code Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center', // Centers items vertically
              justifyContent: 'flex-start', // Aligns content to the start
              gap: 1, // Space between the transparent box and the coupon box
              mb: 1, // Margin below the promo code section
              cursor: 'pointer', // Makes the section clickable
            }}
            onClick={handleCopy}
            WebkitTapHighlightColor="transparent"
          >
            {/* Transparent Box with Coupon Icon */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 18, // Fixed width for the transparent box
                height: 18, // Fixed height for the transparent box
                borderRadius: 1, // Rounded corners
                bgcolor: 'transparent', // Transparent background
              }}
            >
              <CouponIcon
                style={{
                  width: 18,
                  height: 18,
                  flexShrink: 0,
                }}
              />
            </Box>

            {/* Coupon Code Box */}
            <LightTooltip
              title={t('actions.copied')} // Use i18next translated text
              open={tooltipOpen} // Control visibility
              disableFocusListener
              disableHoverListener
              disableTouchListener
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center', // Centers items vertically
                  justifyContent: 'space-between', // Space between the coupon code and the copy icon
                  gap: 1, // Space between elements inside the box
                  borderRadius: 1,
                  height: 40, // Fixed height for the coupon box
                  px: 1, // Horizontal padding
                  py: 1, // Vertical padding
                  bgcolor: 'primary.primary_50', // Colored background for the coupon box
                  width: 125,
                }}
              >
                {/* Coupon Code Text */}
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 20,
                    fontWeight: 500,
                    color: 'primary.main',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {coupon.promo_code}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <CopyIcon
                    style={{
                      width: 20,
                      height: 20,
                      flexShrink: 0,
                      cursor: 'pointer'
                    }}
                  />
                </Box>
              </Box>
            </LightTooltip>
          </Box>
        </Box>
      </Box>

      {/* Like and Dislike Buttons */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          ...(theme?.direction === 'rtl'
            ? { right: 8 }
            : { left: 8 }),
          transform: 'translateY(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2.5,
        }}
      >
        <IconButton
          size="small"
          onClick={handleLike}
          disabled={!canInteract}
          sx={{
            color: isLiked ? 'primary.main' : 'action.disabled',
            '&:hover': { color: 'primary.main' },
          }}
        >
          {getLikeIcon()}
        </IconButton>
        <IconButton
          size="small"
          onClick={handleDislike}
          disabled={!canInteract}
          sx={{
            color: isDisliked ? 'error.main' : 'action.disabled',
            '&:hover': { color: 'error.main' },
          }}
        >
          {getDislikeIcon()}
        </IconButton>
      </Box>
    </Box>
  );
};

export default MobileCoupon;